import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import apiClient from '../../utils/apiClient';
import paths from '../../routes/paths';

const API_BASE = '/identity-service/api/v1/auth/recovery';

const useAccountRecovery = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(() => {
    // Get email from sessionStorage if available
    return sessionStorage.getItem('recoveryEmail') || '';
  });
  const [verificationCode, setVerificationCode] = useState(() => {
    // Get verification code from sessionStorage if available
    return sessionStorage.getItem('recoveryCode') || '';
  });
  const [otpToken, setOtpToken] = useState(() => {
    return sessionStorage.getItem('recoveryOtpToken') || '';
  });
  const [resetToken, setResetToken] = useState(() => {
    return sessionStorage.getItem('recoveryResetToken') || '';
  });

  // Save email to sessionStorage whenever it changes
  useEffect(() => {
    if (email) {
      sessionStorage.setItem('recoveryEmail', email);
    }
  }, [email]);

  // Save verification code to sessionStorage whenever it changes
  useEffect(() => {
    if (verificationCode) {
      sessionStorage.setItem('recoveryCode', verificationCode);
    }
  }, [verificationCode]);

  useEffect(() => {
    if (otpToken) {
      sessionStorage.setItem('recoveryOtpToken', otpToken);
    }
  }, [otpToken]);

  useEffect(() => {
    if (resetToken) {
      sessionStorage.setItem('recoveryResetToken', resetToken);
    }
  }, [resetToken]);

  // Clear recovery data when password is successfully reset
  const clearRecoveryData = () => {
    sessionStorage.removeItem('recoveryEmail');
    sessionStorage.removeItem('recoveryCode');
    sessionStorage.removeItem('recoveryOtpToken');
    sessionStorage.removeItem('recoveryResetToken');
    setEmail('');
    setVerificationCode('');
    setOtpToken('');
    setResetToken('');
  };

  // Step 1: Request password reset (send verification code to email)
  const requestPasswordReset = async (identifier) => {
    const emailAddress = String(identifier || '').trim();
    if (!emailAddress) {
      message.error('Email address is required');
      return { success: false, error: 'Email address is required' };
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post(`${API_BASE}/code/start`, {
        identifier: emailAddress,
      });

      setEmail(emailAddress);
      setOtpToken(
        response.data?.otpToken ||
        response.data?.token ||
        response.data?.recoveryToken ||
        response.data?.resetToken ||
        '',
      );
      message.success('Verification code sent to your email');
      navigate(paths.simpleVerificationCode, {
        state: {
          email: emailAddress,
          otpToken: response.data?.otpToken || response.data?.token || '',
        },
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send verification code';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify the code sent to email
  const verifyCode = async (code) => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) {
      message.error('Verification code is required');
      return { success: false, error: 'Verification code is required' };
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post(`${API_BASE}/code/verify`, {
        identifier: email,
        email,
        otpToken,
        code: trimmedCode,
      });

      const nextResetToken =
        response.data?.resetToken ||
        response.data?.token ||
        response.data?.recoveryToken ||
        '';

      setVerificationCode(trimmedCode);
      if (nextResetToken) {
        setResetToken(nextResetToken);
      }
      message.success('Code verified successfully');
      navigate(paths.simpleResetPassword, {
        state: {
          email,
          resetToken: nextResetToken || resetToken || '',
        },
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Invalid verification code';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset password with new password
  const resetPassword = async (newPassword, confirmPassword) => {
    if (newPassword !== confirmPassword) {
      message.error('Passwords do not match');
      return { success: false, error: 'Passwords do not match' };
    }

    if (newPassword.length < 6) {
      message.error('Password must be at least 6 characters');
      return { success: false, error: 'Password must be at least 6 characters' };
    }

    setIsLoading(true);
    try {
      await apiClient.post(`${API_BASE}/reset`, {
        email,
        code: verificationCode,
        otpToken,
        resetToken,
        newPassword: newPassword,
        confirmPassword: confirmPassword
      });

      clearRecoveryData();
      message.success('Password reset successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    isLoading,
    requestPasswordReset,
    verifyCode,
    resetPassword
  };
};

export default useAccountRecovery;
